// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the bootstrap. Owns the screens and the room, and no game logic whatsoever.
//
// The room is joined ONCE and held until the player goes back to the menu. Every run after the first
// — every "play again" — happens inside it, versioned by rematch.ts. Leaving and re-joining to
// "reset" hands back the room object Trystero is still tearing down, and every player then sits in
// the right room code, alone, drawing on their own.

import '@ben-gy/game-engine/mobile.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl, createLobby, createRoomEntry, mintCode, normalizeRoomCode, setRoomInUrl, type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { DEFAULT_MODE, MODES, MODE_IDS, PER_ROUND, modeOf, type ModeId } from './modes';
import { dailySeed } from './deal';
import { FORMS, PIECES } from './pieces';
import { SIZE, idx, VENT_COUNT } from './plate';
import { restore, snapshot } from './game';
import { Session } from './session';
import { View, type Ghost, type Paint, type TermChip, type TrayChip } from './view';
import { runCountdown } from './countdown';
import { FOOTER_HTML, HOW_HTML, aboutHtml, menuHtml, openPanel, summaryHtml } from './ui';
import type { Summary } from './session';
import './styles/main.css';

const SLUG = 'conduit';

const app = document.getElementById('app')!;
const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  patches: {
    tap: { type: 'square', freq: [520, 520], dur: 0.03, gain: 0.09 },
    aim: { type: 'sine', freq: [300, 340], dur: 0.02, gain: 0.05 },
    turn: { type: 'triangle', freq: [420, 620], dur: 0.06, gain: 0.12 },
    set: { type: 'triangle', freq: [420, 760], dur: 0.1, gain: 0.18 },
    nope: { type: 'square', freq: [190, 120], dur: 0.13, gain: 0.15 },
    seal: { type: 'sine', freq: [660, 1180], dur: 0.26, gain: 0.24 },
    first: { type: 'triangle', freq: [780, 1320], dur: 0.22, gain: 0.24 },
    late: { type: 'sine', freq: [300, 240], dur: 0.16, gain: 0.16 },
    undo: { type: 'sine', freq: [360, 260], dur: 0.08, gain: 0.12 },
    tick: { type: 'triangle', freq: [660, 660], dur: 0.08, gain: 0.16 },
    go: { type: 'triangle', freq: [880, 1320], dur: 0.22, gain: 0.22 },
    over: { type: 'triangle', freq: [560, 320], dur: 0.5, gain: 0.2 },
  },
});
const play = (n: string): void => {
  try {
    sfx.play(n);
  } catch {
    /* audio is decoration; never let it take the game down */
  }
};

let mode: ModeId = modeOf(store.get<string>('mode', DEFAULT_MODE)).id;

interface RoundOpts { mode: string }

let net: Net | null = null;
let rounds: Rounds<RoundOpts> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let view: View | null = null;
let roomCode = '';
let iMintedIt = false;
let beat: ReturnType<typeof setInterval> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let countdown: { cancel: () => void } | null = null;
let deepLinkUsed = false;
let sendWire: (m: unknown) => void = () => {};

// ── the armed fitting, which is UI state and nothing else ─────────────────────────────────────
let armed: number | null = null;
let aim = { x: 3, y: 3 };
let rotIdx = 0;
let fresh: number[] = [];
let lastSealed = 0;

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};

function clearScreen(): void {
  countdown?.cancel();
  countdown = null;
  view?.destroy();
  view = null;
  if (ticker) clearInterval(ticker);
  ticker = null;
  document.removeEventListener('keydown', onKey);
  app.replaceChildren();
}

function onKey(e: KeyboardEvent): void {
  if (!view) return;
  if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
  if (view.handleKey(e)) e.preventDefault();
}

// ── screens ───────────────────────────────────────────────────────────────────────────────────

function showMenu(): void {
  clearScreen();
  setPlaying(false);
  const best = store.get<number>(`best:${mode}`, 0) ?? 0;
  const streak = store.get<number>('streak', 0) ?? 0;
  app.innerHTML = menuHtml(mode, sfx.muted(), best, streak);

  app.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      mode = modeOf(b.dataset.mode).id;
      store.set('mode', mode);
      showMenu();
    }),
  );
  app.querySelector('#playSolo')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    startSolo();
  });
  app.querySelector('#playFriends')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    showRoomEntry();
  });
  app.querySelector('#howBtn')!.addEventListener('click', () => openPanel(app, HOW_HTML));
  app.querySelector('#aboutBtn')!.addEventListener('click', () => openPanel(app, aboutHtml()));
  const mute = app.querySelector<HTMLButtonElement>('#muteBtn')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  });

  if (!store.get<boolean>('seenHow', false)) {
    store.set('seenHow', true);
    openPanel(app, HOW_HTML);
  }
}

/** Solo runs the UTC daily seed, so everybody in the world gets the same plate on the same date. */
function startSolo(): void {
  buildSession({ mode, seed: dailySeed(MODES[mode]), match: 1, peers: [], selfId: 'you', myName: 'You' });
}

// ── the room ──────────────────────────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  createRoomEntry({
    container: host,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link — or scan the code, or type it in.',
    onSubmit: (code, created) => {
      roomCode = normalizeRoomCode(code);
      iMintedIt = created;
      enterRoom();
    },
    onCancel: () => showMenu(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function enterRoom(): void {
  clearScreen();
  setRoomInUrl(roomCode);
  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: iMintedIt },
    {
      // NEVER navigate from a net handler: these fire in the lobby AND just after a run ends.
      onPeers: () => {
        lobby?.repaint();
        session?.reannounce();
        repaint();
      },
      onPeerLeave: (id: PeerId) => {
        session?.peerLeft(id);
        lobby?.repaint();
        repaint();
      },
      onHostChange: () => {
        lobby?.repaint();
        session?.reannounce();
        repaint();
      },
    },
  );

  const wire = net.channel<unknown>('cdt', (m, from) => session?.receive(m, from));
  sendWire = (m) => wire(m);

  rounds = createRounds<RoundOpts>({
    net,
    playerName: store.get<string>('name', '') || `Fitter ${Math.floor(Math.random() * 90 + 10)}`,
    minPlayers: 2,
    roundOpts: () => ({ mode }),
    onRound: (info) => {
      // The HOST's pick is what the room draws, frozen inside the start, so two peers can never be
      // working different plates.
      const m = modeOf(info.opts?.mode).id;
      if (!info.seated) {
        showSpectator();
        return;
      }
      startRound({
        mode: m,
        seed: info.seed,
        match: info.round,
        peers: info.players.map((p) => ({ id: p.id, name: p.name })),
        selfId: net!.selfId,
        myName: info.players.find((p) => p.id === net!.selfId)?.name ?? 'You',
      });
    },
    onChange: () => lobby?.repaint(),
  });

  showLobby();
}

function showLobby(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  lobby = createLobby({
    container: host,
    net: net!,
    rounds: rounds!,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    modeSlot: () => {
      const st = rounds!.state();
      // Guests see the HOST's gossiped choice. Rendering your own pick and labelling it the host's
      // is a confident lie, and it has shipped in this fleet before.
      const chosen = st.isHost ? mode : modeOf(st.hostOpts?.mode).id;
      return `<div class="modeslot"><span class="modeslot-label">Mode</span>
        <div class="segmented small">${MODE_IDS.map(
          (id) =>
            `<button type="button" class="seg${id === chosen ? ' on' : ''}" data-lmode="${id}"${
              st.isHost ? '' : ' disabled'
            }>${MODES[id].name}</button>`,
        ).join('')}</div>
        <p class="fine">${st.isHost ? MODES[mode].blurb : 'The host picks the mode.'}</p></div>`;
    },
    onModeMount: () => {
      host.querySelectorAll<HTMLButtonElement>('[data-lmode]').forEach((b) =>
        b.addEventListener('click', () => {
          mode = modeOf(b.dataset.lmode).id;
          store.set('mode', mode);
          lobby?.repaint();
        }),
      );
    },
    onCancel: () => leaveRoom(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function showSpectator(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = `<div class="main-content"><h2>A run is already under way</h2>
    <p class="fine">You are in the next one. Hang on — it will start on its own.</p></div>${FOOTER_HTML}`;
}

/**
 * Leaving is the ONLY place the room is torn down, and it clears `?room=` on the way out. Leave it
 * in the URL and a reload — or reopening from a home-screen icon — silently drags the player back
 * into a room they left, with no way to start a new one.
 */
function leaveRoom(): void {
  if (beat) clearInterval(beat);
  beat = null;
  session?.destroy();
  session = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  void net?.leave();
  net = null;
  clearRoomInUrl();
  deepLinkUsed = true;
  showMenu();
}

// ── a run ─────────────────────────────────────────────────────────────────────────────────────

interface StartArgs {
  mode: ModeId;
  seed: number;
  match: number;
  peers: Array<{ id: string; name: string }>;
  selfId: string;
  myName: string;
}

function startRound(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const holder = document.createElement('div');
  holder.className = 'countwrap';
  app.append(holder);
  countdown = runCountdown({
    container: holder,
    sfx: play,
    onDone: () => {
      holder.remove();
      buildSession(args);
    },
  });
}

function buildSession(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  armed = null;
  rotIdx = 0;
  fresh = [];
  lastSealed = 0;

  session = new Session({
    mode: MODES[args.mode],
    seed: args.seed,
    match: args.match,
    selfId: args.selfId,
    myName: args.myName,
    peers: args.peers,
    deps: {
      send: (m) => sendWire(m),
      onChange: () => repaint(),
      onEnd: (s) => showResults(s),
    },
  });

  view = new View({
    onArm: (slot) => armSlot(slot),
    onAim: (x, y) => aimAt(x, y),
    onRotate: () => rotate(),
    onSet: () => commit(),
    onUndo: () => undo(),
    onHelp: () => openPanel(app, HOW_HTML),
    onQuit: () => {
      // Back to the lobby, never out of the room — and rounds.finish() FIRST, because the engine
      // lobby paints nothing at all while the round is still 'playing' and this seat is seated.
      if (net) {
        rounds?.finish();
        showLobby();
      } else showMenu();
    },
    onNudge: (dx, dy) => aimAt(aim.x + dx, aim.y + dy),
  });
  app.append(view.root);
  document.addEventListener('keydown', onKey);

  autoArm();

  if (beat) clearInterval(beat);
  // setInterval, never rAF: a backgrounded tab must keep its heartbeat alive, or a peer who tabs
  // away is indistinguishable from one who closed the lid.
  beat = setInterval(() => session?.beat(), 3000);
  repaint();
}

// ── arming and aiming ─────────────────────────────────────────────────────────────────────────

/** Arm a sensible fitting and drop the ghost somewhere legal, so a turn never starts on a blank. */
function autoArm(): void {
  const s = session;
  if (!s || s.game.over) return;
  const av = s.game.available();
  if (av.length === 0) return;
  // Prefer a fitting that actually has somewhere to go.
  const withSpots = av.find((slot) => s.game.spots(slot).length > 0);
  armSlot(withSpots ?? av[0], false);
}

function armSlot(slot: number, click = true): void {
  const s = session;
  if (!s || s.game.over) return;
  if (!s.game.available().includes(slot)) return;
  armed = slot;
  const spots = s.game.spots(slot);
  if (spots.length > 0) {
    // Land on the best-scoring legal cell rather than a corner, so the player is never staring at a
    // plate wondering where anything is allowed to go.
    const best = bestSpot(slot);
    aim = { x: best.x, y: best.y };
    rotIdx = 0;
    const rots = s.game.legalRotsAt(slot, aim.x, aim.y);
    rotIdx = Math.max(0, rots.indexOf(best.rot));
  }
  if (click) play('tap');
  repaint();
}

/**
 * Where to drop the ghost when a fitting is armed, so the player never faces a blank plate.
 *
 * Trials snapshot and restore — they must NOT use the player's undo, which refuses once a placement
 * has rolled the round over and would therefore leave the fourth trial of every round permanently
 * drawn on the plate.
 */
function bestSpot(slot: number): { x: number; y: number; rot: number } {
  const s = session!;
  const spots = s.game.spots(slot);
  if (spots.length === 0) return { x: aim.x, y: aim.y, rot: 0 };
  const before = snapshot(s.game);
  let best = spots[0];
  let bestV = -Infinity;
  for (const sp of spots) {
    if (!s.game.place(slot, sp.rot, sp.x, sp.y)) continue;
    const v = s.game.score().total;
    restore(s.game, before);
    if (v > bestV) {
      bestV = v;
      best = sp;
    }
  }
  return best;
}

function aimAt(x: number, y: number): void {
  const s = session;
  if (!s || armed === null) return;
  const nx = clamp(x, 0, SIZE - 1);
  const ny = clamp(y, 0, SIZE - 1);
  if (nx === aim.x && ny === aim.y) return;
  aim = { x: nx, y: ny };
  const rots = s.game.legalRotsAt(armed, nx, ny);
  // AUTO-ORIENT, restricted strictly to the one-legal-rotation case. A convenience that silently
  // picks between several options has removed a decision rather than simplified one.
  if (rots.length === 1) rotIdx = 0;
  else if (rotIdx >= Math.max(rots.length, 1)) rotIdx = 0;
  play('aim');
  repaint();
}

function currentRots(): number[] {
  const s = session;
  if (!s || armed === null) return [];
  const legal = s.game.legalRotsAt(armed, aim.x, aim.y);
  if (legal.length > 0) return legal;
  const p = s.game.pieceAt(armed);
  return p ? FORMS[p].map((_, i) => i) : [];
}

function rotate(): void {
  const rots = currentRots();
  if (rots.length <= 1) return;
  rotIdx = (rotIdx + 1) % rots.length;
  play('turn');
  repaint();
}

function commit(): void {
  const s = session;
  if (!s || armed === null || s.game.over) return;
  const rots = currentRots();
  // Clamp rather than index blindly: the legal-rotation list changes as the ghost moves, and a stale
  // index would silently commit rotation 0 — which is usually illegal, so SET would appear dead.
  const rot = rots[Math.min(rotIdx, rots.length - 1)] ?? 0;
  const before = s.game.sealedIn.filter((r) => r >= 0).length;
  if (!s.game.place(armed, rot, aim.x, aim.y)) {
    play('nope');
    // Never dead-end on a refused commit: re-arm so the ghost lands somewhere legal again.
    autoArm();
    repaint();
    return;
  }
  fresh = [idx(aim.x, aim.y)];
  const after = s.game.sealedIn.filter((r) => r >= 0).length;
  if (after > before) {
    play('seal');
    lastSealed = after;
  } else play('set');
  armed = null;
  s.afterMark();
  if (!s.game.over) autoArm();
}

function undo(): void {
  const s = session;
  if (!s || !s.game.canUndo) return;
  s.game.undo();
  play('undo');
  autoArm();
  repaint();
}

// ── painting ──────────────────────────────────────────────────────────────────────────────────

function repaint(): void {
  const s = session;
  if (!view || !s) return;
  const g = s.game;
  const detail = g.score();

  const legalCells = new Map<number, number>();
  if (armed !== null) {
    for (const sp of g.spots(armed)) {
      const i = idx(sp.x, sp.y);
      const cost = g.weepCountAt(armed, sp.rot, sp.x, sp.y);
      const prev = legalCells.get(i);
      if (prev === undefined || cost < prev) legalCells.set(i, cost);
    }
  }

  const rots = currentRots();
  const rot = rots[rotIdx] ?? 0;
  const piece = armed === null ? null : g.pieceAt(armed);
  const refusal = armed === null ? null : g.refusalAt(armed, rot, aim.x, aim.y);
  const ghost: Ghost | null =
    piece && armed !== null
      ? {
          slot: armed,
          piece,
          rot,
          x: aim.x,
          y: aim.y,
          legal: refusal === null,
          cost: g.weepCountAt(armed, rot, aim.x, aim.y),
        }
      : null;

  const tray: TrayChip[] = [0, 1, 2, 3].map((slot) => {
    const p = g.pieceAt(slot);
    const visible = g.available().includes(slot);
    const spent = !g.remaining.includes(slot);
    return {
      slot,
      piece: p ?? 'cs',
      armed: armed === slot,
      spent,
      hidden: !spent && !visible,
    };
  });

  const terms: TermChip[] = g.mode.ventsScore
    ? [
        { key: 'c', label: 'claim', value: detail.claim },
        { key: 'n', label: 'nets', value: detail.net },
        { key: 'r', label: 'run', value: detail.run },
        { key: 'e', label: 'ends', value: detail.ends, bad: detail.ends < 0 },
      ]
    : [
        { key: 'r', label: 'run', value: detail.run },
        { key: 'o', label: 'stray', value: detail.offPath, bad: detail.offPath < 0 },
        { key: 'e', label: 'ends', value: detail.ends, bad: detail.ends < 0 },
      ];
  if (g.mode.relaxed) terms.push({ key: 'w', label: 'weeps', value: detail.weep, bad: detail.weep < 0 });

  const stuck = armed !== null && g.spots(armed).length === 0;
  const paint: Paint = {
    plate: g.plate,
    ghost,
    legalCells,
    vents: s.ventStates(),
    round: g.round,
    rounds: g.mode.rounds,
    spent: g.spentThisRound,
    score: detail.total,
    par: s.par(),
    terms,
    tray,
    rots,
    rotIndex: rotIdx,
    canSet: refusal === null && !g.over,
    setLabel: stuck ? 'Scrap' : 'Set',
    reason: reasonFor(refusal),
    canUndo: g.canUndo,
    note: noteFor(),
    fresh,
    rivals: s.seats.map((seat) => ({
      name: seat.name,
      glyph: seat.glyph,
      ink: seat.ink,
      marks: seat.marks,
      score: seat.sc,
      away: !seat.you && !seat.bot && Date.now() - seat.heard > 20000,
      you: seat.you,
    })),
  };
  view.render(paint);
  fresh = [];
}

/** The refusal, in plain words. This is the only place the rules are ever explained to a player. */
function reasonFor(r: ReturnType<NonNullable<typeof session>['game']['refusalAt']> | null): string {
  if (!r) return '';
  const dir = ['north', 'east', 'south', 'west'];
  switch (r.kind) {
    case 'taken':
      return 'That square is taken.';
    case 'match':
      return `${cap(gauge(r.mine))} meets ${gauge(r.theirs)} on the ${dir[r.dir]} side.`;
    case 'margin':
      return `That ${gauge(r.mine)} end runs off the plate on the ${dir[r.dir]} side.`;
    case 'splice':
      return 'Nothing to splice onto — it must join what you have drawn, or start at a vent.';
    default:
      return '';
  }
}

const gauge = (g: number): string => (g === 1 ? 'culvert' : g === 2 ? 'filament' : 'a solid side');
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function noteFor(): string {
  const s = session;
  if (!s) return '';
  if (armed === null) return 'Pick a fitting.';
  const g = s.game;
  if (g.spots(armed).length === 0) return 'This one fits nowhere — scrap it.';
  const sealed = g.sealedIn.filter((r) => r >= 0).length;
  if (sealed > 0 && sealed === lastSealed) return `${sealed} vent${sealed === 1 ? '' : 's'} sealed.`;
  return `${PIECES[g.pieceAt(armed) ?? 'cs'].name} · ${g.spentThisRound}/${PER_ROUND} this round`;
}

// ── the end ───────────────────────────────────────────────────────────────────────────────────

function showResults(s: Summary): void {
  play('over');
  setPlaying(false);
  const sess = session;
  if (sess && !net) {
    const mine = s.lines.find((l) => l.you);
    const best = store.get<number>(`best:${sess.mode.id}`, 0) ?? 0;
    if (mine && mine.total > best) store.set(`best:${sess.mode.id}`, mine.total);
    bumpStreak();
  }
  clearScreen();
  app.innerHTML = summaryHtml(s);

  const again = app.querySelector<HTMLButtonElement>('#againBtn')!;
  const toLobby = app.querySelector<HTMLButtonElement>('#lobbyBtn')!;
  const wait = app.querySelector<HTMLElement>('#resWait')!;

  if (net && rounds) {
    rounds.finish();
    toLobby.hidden = false;
    toLobby.addEventListener('click', () => showLobby());
    again.addEventListener('click', () => {
      rounds!.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
    });
    // A visible reason and a horizon. One player still reading the table must not hold the room.
    ticker = setInterval(() => {
      const st = rounds?.state();
      if (!st) return;
      wait.hidden = false;
      wait.textContent =
        st.startsInMs !== null
          ? `Next run in ${Math.ceil(st.startsInMs / 1000)}s…`
          : `${st.votes.length} of ${st.present.length} ready.`;
      // Fold in plates that arrive after the table first painted.
      const live = session?.summary();
      if (live && !live.waiting) {
        const el = app.querySelector('#resWaitPlates');
        if (el) el.remove();
      }
    }, 400);
  } else {
    again.addEventListener('click', () => {
      play('tap');
      startSolo();
    });
  }
  app.querySelector('#menuBtn')!.addEventListener('click', () => (net ? leaveRoom() : showMenu()));
  app
    .querySelector('.results-feedback')!
    .addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
}

function bumpStreak(): void {
  const today = new Date().toISOString().slice(0, 10);
  const last = store.get<string>('lastPlayed', '') ?? '';
  if (last === today) return;
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  store.set('streak', last === y ? (store.get<number>('streak', 0) ?? 0) + 1 : 1);
  store.set('lastPlayed', today);
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ── boot ──────────────────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();
  // ONCE, before any mesh exists. Trystero builds one global connection pool from the first room's
  // config, so a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fails open — TURN is an upgrade, never a dependency */
  }

  // A `?room=` is honoured ONCE. After that, "play with friends" always offers create-or-join, so a
  // reload can never drag somebody back into a room they have left.
  const deep = new URL(location.href).searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    roomCode = normalizeRoomCode(deep);
    iMintedIt = false;
    enterRoom();
    return;
  }
  showMenu();
}

window.addEventListener('beforeunload', () => void net?.leave());
void boot();

// Exposed for the browser verification pass: drives a run synchronously in a tab where rAF and real
// timers are throttled. Never used by the game itself.
(window as unknown as Record<string, unknown>).__conduit = {
  state: () =>
    session && {
      round: session.game.round,
      rounds: session.game.mode.rounds,
      spent: session.game.spentThisRound,
      over: session.game.over,
      score: session.game.score().total,
      sealed: session.game.sealedIn.filter((r) => r >= 0).length,
      mode: session.game.mode.id,
    },
  plate: () => session && Array.from(session.game.plate.piece),
  armed: () => armed,
  aim: () => aim,
  legal: () => (armed === null ? 0 : session?.game.spots(armed).length),
  arm: (s: number) => armSlot(s),
  aimAt: (x: number, y: number) => aimAt(x, y),
  rotate: () => rotate(),
  set: () => commit(),
  /** Play the whole run by taking the auto-armed suggestion every time. */
  auto: (n = 40) => {
    for (let k = 0; k < n && session && !session.game.over; k++) {
      if (armed === null) autoArm();
      commit();
    }
    return session?.game.over;
  },
  mint: () => mintCode(),
  modes: MODE_IDS,
  vents: VENT_COUNT,
};
